import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { decodeGlobalId } from '~/utils/id'
import visit from '~/utils/visit'
import { useRouter } from 'next/router'
import { Block, BlockHeader, BlockSubtitle } from '~/components/Block'
import qs from 'querystringify'
import { useViewer } from '~/components/ViewerContext'
import { ADD_TICKETS_TO_CART } from './addTicketsToCart'
import { GET_VIEWERS_ACTIVE_TICKET_ALERTS } from './getViewersActiveTicketAlerts'
import {
  Avatar,
  fontSize,
  shadow,
  space,
  device,
  Button,
  Text,
  H2,
  color,
  Alert,
  radius,
  fontWeight,
  Tooltip,
} from '@ticketswap/solar'
import { Minus, Plus, InfoRounded } from '@ticketswap/comets'
import PriceInformationDialog from '../PriceInformationDialog'
import { createMarkup, formatMoney, getLinkProps, track } from '../../utils'
import useLocalStorage from '~/hooks/useLocalStorage'
import AuthDialog from '../AuthDialog'
import { AUTHENTICATION_VIEWED, PRODUCT_ADDED } from '~/lib/tracking-events'
import EventWarningAlert from '../EventWarningAlert'
import SeatSelection from './SeatSelection'
import TicketAlertsManager from '../TicketAlertsManager'
import AppsAdoptionAlert from '../AppsAdoptionAlert'
import Details from './Details'

const Container = styled.div`
  margin-bottom: ${space[32]};

  @media ${device.tablet} {
    margin-bottom: ${space[64]};
  }
`

const StyledBlock = styled(Block)`
  margin-top: ${space[24]};
  margin-bottom: 0;

  @media ${device.tablet} {
    margin-top: ${space[48]};
    margin-bottom: 0;
  }
`

const ItemWrapper = styled.div`
  margin-bottom: ${space[8]};

  @media ${device.tablet} {
    margin-bottom: ${space[16]};
  }
`

const AvatarContainer = styled.div`
  margin-right: ${space[16]};
`

const Comment = styled.div`
  background-color: ${color.stardust};
  border-radius: ${radius.lg};
  padding: ${space[16]};
  display: flex;

  strong {
    display: block;
    font-weight: ${fontWeight.bold};
  }
`

const Panel = styled.div`
  padding: ${space[16]};
  background-color: white;
  box-shadow: ${shadow.strong};
  border-radius: ${radius.lg};
  position: relative;
  z-index: 5;
  margin-top: -${space[16]};
  margin-bottom: ${space[16]};

  @media ${device.tablet} {
    margin-top: -${space[32]};
  }
`

const PanelBody = styled.div`
  & > * + * {
    margin-top: ${space[16]};
  }
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  position: relative;

  @media ${device.tablet} {
    flex-direction: ${props => (props.flex ? 'column' : 'row')};
  }
`

const QuantitySelector = styled.div`
  width: 50%;
  display: flex;
  align-items: center;

  button {
    width: ${space[44]};
    height: ${space[44]};
  }

  @media ${device.tablet} {
    width: 33.3%;
    width: calc(100% / 3);

    button {
      width: ${space[56]};
      height: ${space[56]};
    }
  }
`

const Quantity = styled.div`
  font-variant-numeric: tabular-nums;
  padding-left: ${space[8]};
  padding-right: ${space[8]};

  @media ${device.tablet} {
    padding-left: ${space[16]};
    padding-right: ${space[16]};
  }
`

const Price = styled.div`
  height: ${space[44]};
  display: flex;
  align-items: center;
  margin-top: 0;
  position: ${props => (props.withSeating ? 'relative' : 'absolute')};
  width: ${props => (props.withSeating ? '100%' : '50%')};
  justify-content: ${props => (props.withSeating ? 'center' : 'flex-end')};
  top: 0;
  right: 0;

  @media ${device.tablet} {
    position: relative;
    align-self: initial;
    width: ${props => (props.withSeating ? '100%' : '33.3%')};
    width: ${props => (props.withSeating ? '100%' : 'calc(100% / 3)')};
    align-items: flex-start;
    justify-content: center;
    height: auto;
    margin-top: 0;
    /* Optical adjustment */
    transform: translateY(${props => (props.withSeating ? 1 : -1)}px);
  }
`

const InfoButton = styled.button`
  color: ${color.spaceLighter};
  margin-left: ${space[8]};
  transform: translateY(-2px);

  @media ${device.tablet} {
    transform: translateY(4px);
  }
`

const BuyButtonContainer = styled.div`
  width: 100%;
  margin-top: ${props => (props.withSeating ? space[16] : space[8])};

  button {
    width: 100%;
  }

  @media ${device.tablet} {
    margin-top: ${props => (props.withSeating ? space[16] : 0)};
    text-align: right;
    width: ${props => (props.withSeating ? '100%' : '33.3%')};
    width: ${props => (props.withSeating ? '100%' : 'calc(100% / 3)')};

    button {
      width: ${props => (props.withSeating ? '100%' : 'auto')};
    }
  }
`

const OriginalPrice = styled.p`
  font-size: ${fontSize[14]};
  text-align: center;
  color: ${color.spaceLight};
  margin-top: ${space[8]};

  @media ${device.tablet} {
    position: ${props => (props.withSeating ? 'relative' : 'absolute')};
    bottom: 0;
    left: 50%;
    transform: translateX(-50%) translateY(1px);
  }
`

function Purchaser({ t, listing, status, ...props }) {
  const router = useRouter()
  const query = qs.parse(router.asPath.split('?')[1])
  const { viewer: currentViewer } = useViewer()
  const [, setRecentlyVisitedListing] = useLocalStorage(
    'recently_visited_listing',
    null
  )
  const mutated = React.useRef(false)
  const submitButtonRef = React.useRef()
  const [quantity, setQuantity] = React.useState(
    query && query.addToCart ? query.addToCart : 1
  )
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(null)
  const [canSubmit, setCanSubmit] = React.useState(currentViewer)
  const initialTicketSelection = listing?.tickets?.edges
    .filter(({ node }) => node.status === 'AVAILABLE')
    .map(({ node }) => node.id)
  const [ticketSelection, setTicketSelection] = React.useState(
    query && query.addTicketsToCart
      ? query.addTicketsToCart.split(',')
      : initialTicketSelection
  )
  const numberOfTicketsFromListingInUsersCart =
    props.ticketsFromListingInUsersCart.length
  const availableTickets = listing?.tickets?.edges.filter(
    ({ node }) => node.status === 'AVAILABLE'
  )
  const numberOfAvailableTickets = availableTickets && availableTickets.length
  const showScarcityAlert =
    listing.numberOfTicketsInListing > 1 &&
    numberOfAvailableTickets !== listing.numberOfTicketsInListing
  const ticketsHaveSeatsAssigned =
    listing?.tickets?.edges.filter(({ node }) => node.seating).length > 0
  const previousPrice = query?.amount && parseFloat(query.amount)
  const { id, event = {}, eventType = {}, price = {} } = listing || {}
  const {
    sellerPrice,
    serviceFeeForBuyer,
    transactionFeePerTicket,
    totalPriceWithTransactionFee,
  } = price
  const { href: cartHref } = getLinkProps('CART')
  const { data = {} } = useQuery(GET_VIEWERS_ACTIVE_TICKET_ALERTS, {
    skip: !canSubmit,
  })
  const { viewer } = data
  const isSoldDeletedOrReserved =
    status.listingIsSold || status.listingIsDeleted || status.listingIsReserved
  const showEditOptions =
    viewer?.id === listing && listing?.seller?.id && !isSoldDeletedOrReserved
  const ticketAlertsForCurrentListing = alert =>
    alert?.event?.id === listing?.event?.id ||
    alert?.eventType?.id === listing?.eventType?.id
  const userHasActiveTicketAlertsForCurrentListing = viewer
    ? viewer.activeTicketAlerts.filter(ticketAlertsForCurrentListing).length > 0
    : query && query.reference === 'wanted'
  const expiredEventOrEventType =
    status.eventIsExpired || status.eventTypeIsExpired
  const showTicketAlertsManager =
    status.listingIsSold &&
    !expiredEventOrEventType &&
    listing.eventType &&
    listing.event
  const [addToCart] = useMutation(ADD_TICKETS_TO_CART)

  React.useEffect(() => {
    // React to `?addToCart` and `?addTicketsToCart` URL params
    if (
      mutated.current === false &&
      (query?.addToCart || query?.addTicketsToCart)
    ) {
      submitButtonRef.current && submitButtonRef.current.click()
    }
  }, [mutated, query])

  async function handleSubmit(e) {
    e.preventDefault()
    if (loading) return false
    setError(null)
    setLoading(true)
    mutated.current = true

    const {
      data: { addTicketsToCart },
    } = await addToCart({
      variables: {
        input: {
          listingId: listing.id,
          listingHash: listing.hash,
          ...(!ticketsHaveSeatsAssigned && {
            amountOfTickets: quantity,
          }),
          ...(ticketsHaveSeatsAssigned && {
            ticketIds: ticketSelection,
          }),
        },
      },
    })

    if (addTicketsToCart?.errors.length > 0) {
      setLoading(false)
      return setError(addTicketsToCart.errors[0])
    }

    track(PRODUCT_ADDED, {
      product_id: decodeGlobalId(eventType ? eventType.id : event.id),
      name: eventType?.title,
      brand: event.name,
      price: totalPriceWithTransactionFee.formattedAmount,
      quantity,
      variant: decodeGlobalId(id),
    })

    // Store this listing in localStorage so
    // we can link back to it after clicking
    // the `Exit`-button of the checkout.
    setRecentlyVisitedListing({
      slug: listing.event.slug,
      id: decodeGlobalId(listing.id),
      hash: listing.hash,
    })

    // Redirect
    visit({ href: cartHref })
  }

  return (
    <Container>
      {!status.listingHasAvailableTickets ? (
        <StyledBlock>
          <BlockHeader>
            <H2
              dangerouslySetInnerHTML={createMarkup(
                t(
                  `listing:purchaser.${
                    status.eventIsExpired
                      ? 'expiredEvent'
                      : status.eventTypeIsExpired
                      ? 'expiredEventType'
                      : status.listingIsSold
                      ? 'soldListing'
                      : status.listingIsDeleted
                      ? 'deletedListing'
                      : 'reservedListing'
                  }.title`
                ),
                {
                  renderInline: true,
                }
              )}
            />

            {isSoldDeletedOrReserved && (
              <BlockSubtitle
                dangerouslySetInnerHTML={createMarkup(
                  t(
                    `listing:purchaser.${
                      status.listingIsSold
                        ? userHasActiveTicketAlertsForCurrentListing
                          ? 'soldListing.text.withTicketAlerts'
                          : 'soldListing.text.regular'
                        : status.listingIsDeleted
                        ? 'deletedListing.text'
                        : 'reservedListing.text'
                    }`
                  ),
                  {
                    renderInline: true,
                  }
                )}
              />
            )}
          </BlockHeader>

          {showTicketAlertsManager && (
            <ItemWrapper>
              <TicketAlertsManager eventId={event.id} />
            </ItemWrapper>
          )}

          {(!currentViewer || currentViewer?.userDevices?.length === 0) && (
            <ItemWrapper>
              <AppsAdoptionAlert
                title={t('listing:appsAdoption.listingSold.title')}
                text={t('listing:appsAdoption.listingSold.text', {
                  eventName: event.name,
                })}
                trackingContext="SOLD_LISTING"
              />
            </ItemWrapper>
          )}
        </StyledBlock>
      ) : (
        <Panel>
          <PanelBody>
            <Form onSubmit={handleSubmit} flex={ticketsHaveSeatsAssigned}>
              {ticketsHaveSeatsAssigned ? (
                <SeatSelection
                  t={t}
                  listing={listing}
                  initialSelection={initialTicketSelection}
                  onChange={selection => setTicketSelection(selection)}
                />
              ) : (
                <QuantitySelector>
                  <Button
                    rounded
                    type="button"
                    square
                    variant="secondary"
                    disabled={quantity === 1}
                    onClick={() => setQuantity(qty => Math.max(1, qty - 1))}
                  >
                    <Minus />
                  </Button>
                  <Quantity>
                    <H2 as="span">{quantity}</H2>
                  </Quantity>
                  <Tooltip
                    label={
                      showScarcityAlert
                        ? t('listing:purchaser.scarcityAlert', {
                            x: numberOfAvailableTickets,
                            y: listing.numberOfTicketsInListing,
                          })
                        : t('listing:purchaser.availableTickets', {
                            count: numberOfAvailableTickets,
                          })
                    }
                  >
                    {/* This div makes sure that the tooltip will show even if the button is disabled */}
                    <div>
                      <Button
                        disabled={
                          quantity === listing.numberOfTicketsStillForSale
                        }
                        onClick={() => {
                          setQuantity(qty =>
                            Math.min(
                              listing.numberOfTicketsStillForSale,
                              qty + 1
                            )
                          )
                        }}
                        data-testid="increase-qty"
                        rounded
                        square
                        type="button"
                        variant="secondary"
                      >
                        <Plus />
                      </Button>
                    </div>
                  </Tooltip>
                </QuantitySelector>
              )}

              <Price withSeating={ticketsHaveSeatsAssigned}>
                <H2 as="span" data-testid="total-price">
                  {formatMoney(
                    totalPriceWithTransactionFee,
                    ticketsHaveSeatsAssigned ? ticketSelection.length : quantity
                  )}
                </H2>
                <PriceInformationDialog
                  t={t}
                  ticketQuantity={parseInt(
                    ticketsHaveSeatsAssigned ? ticketSelection.length : quantity
                  )}
                  ticketDescription={`${listing?.event?.name} – ${listing?.eventType?.title}`}
                  priceWithoutFees={formatMoney(
                    sellerPrice,
                    ticketsHaveSeatsAssigned ? ticketSelection.length : quantity
                  )}
                  serviceFee={formatMoney(
                    serviceFeeForBuyer,
                    ticketsHaveSeatsAssigned ? ticketSelection.length : quantity
                  )}
                  transactionFee={formatMoney(
                    transactionFeePerTicket,
                    ticketsHaveSeatsAssigned ? ticketSelection.length : quantity
                  )}
                  totalPrice={formatMoney(
                    totalPriceWithTransactionFee,
                    ticketsHaveSeatsAssigned ? ticketSelection.length : quantity
                  )}
                  withExplanation={true}
                >
                  {({ show }) => (
                    <InfoButton
                      data-testid="price-info-toggle"
                      type="button"
                      onClick={show}
                    >
                      <InfoRounded size={24} />
                    </InfoButton>
                  )}
                </PriceInformationDialog>
              </Price>

              <BuyButtonContainer withSeating={ticketsHaveSeatsAssigned}>
                <AuthDialog
                  onToggle={on => on && track(AUTHENTICATION_VIEWED)}
                  onComplete={() => {
                    // Bit hairy this implementation. Think of an improvement.
                    setCanSubmit(true)
                    submitButtonRef.current.click()
                  }}
                  emailLoginRedirectUrl={`${
                    global.location && global.location.href
                  }${
                    ticketsHaveSeatsAssigned
                      ? `?addTicketsToCart=${ticketSelection.join(',')}`
                      : `?addToCart=${quantity}`
                  }`}
                >
                  {({ show }) => (
                    <Button
                      ref={submitButtonRef}
                      loading={loading}
                      disabled={
                        ticketsHaveSeatsAssigned && ticketSelection.length === 0
                      }
                      type={canSubmit ? 'submit' : 'button'}
                      width="full"
                      onClick={canSubmit ? () => {} : show}
                    >
                      {(!ticketsHaveSeatsAssigned && quantity > 1) ||
                      (ticketsHaveSeatsAssigned && ticketSelection.length > 1)
                        ? t('listing:purchaser.buyTicket_plural')
                        : t('listing:purchaser.buyTicket')}
                    </Button>
                  )}
                </AuthDialog>
              </BuyButtonContainer>

              <OriginalPrice withSeating={ticketsHaveSeatsAssigned}>
                {t('listing:purchaser.originalPrice', {
                  amount: listing?.price?.originalPrice?.formattedAmount,
                })}
              </OriginalPrice>
            </Form>

            {listing.description && (
              <Comment>
                <AvatarContainer>
                  <Avatar src={listing.seller.avatarBig} size={52} />
                </AvatarContainer>
                <Text>
                  <strong>{t('listing:purchaser.sellerComments')}</strong>
                  {listing.description}
                </Text>
              </Comment>
            )}

            <EventWarningAlert event={event} />

            {error && <Alert variant="error">{error.message}</Alert>}

            {previousPrice &&
            previousPrice !== totalPriceWithTransactionFee.amount ? (
              <Alert
                variant={
                  previousPrice > totalPriceWithTransactionFee.amount
                    ? 'info'
                    : 'warning'
                }
              >
                {t(
                  `listing:purchaser.ticketPriceAlerts.${
                    previousPrice > totalPriceWithTransactionFee.amount
                      ? 'lowerPrice'
                      : 'higherPrice'
                  }`,
                  {
                    oldPrice: query?.price,
                    newPrice: formatMoney(
                      totalPriceWithTransactionFee,
                      quantity
                    ),
                  }
                )}
              </Alert>
            ) : null}

            {showEditOptions && (
              <Alert
                action={{
                  label: t('listing:purchaser.editOptions.button'),
                  onClick: () => {
                    visit(
                      getLinkProps('PROFILE_FOR_SALE_LISTING', {
                        id: decodeGlobalId(listing.id),
                        hash: listing.hash,
                      })
                    )
                  },
                }}
              >
                {t('listing:purchaser.editOptions.text')}
              </Alert>
            )}

            {numberOfTicketsFromListingInUsersCart > 0 && (
              <Alert
                variant="success"
                action={{
                  label: t('listing:purchaser.cartInfo.button'),
                  onClick: () => visit({ href: cartHref }),
                }}
              >
                <span
                  dangerouslySetInnerHTML={createMarkup(
                    t('listing:purchaser.cartInfo.text', {
                      count: numberOfTicketsFromListingInUsersCart,
                    }),
                    { renderInline: true }
                  )}
                />
              </Alert>
            )}
          </PanelBody>
        </Panel>
      )}

      <Details t={t} listing={listing} />
    </Container>
  )
}

Purchaser.defaultProps = {
  listing: {},
  status: {},
  ticketsFromListingInUsersCart: [],
}

Purchaser.propTypes = {
  t: PropTypes.func.isRequired,
  listing: PropTypes.object.isRequired,
  status: PropTypes.object.isRequired,
  ticketsFromListingInUsersCart: PropTypes.array,
}

export default Purchaser
